import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';

import { useOtaStatus, setOtaStatus } from 'src/hooks/use-ota-status';

import {
  applyNow,
  isOtaEnabled,
  checkForUpdate,
  getChannelForRole,
  downloadAndStageUpdate,
} from 'src/utils/ota-updater';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

const SNOOZE_KEY = 'artham.ota.snoozeUntil';
const SNOOZE_MS = 24 * 60 * 60 * 1000;

function isSnoozed(): boolean {
  const until = parseInt(localStorage.getItem(SNOOZE_KEY) ?? '0', 10);
  return !!until && Date.now() < until;
}

/**
 * Globally-mounted OTA runtime. Runs the launch-time (and on-resume) update
 * check, drives the shared status store, and surfaces a non-blocking bottom
 * sheet when an update is staged and ready to apply. No-op on web / dev.
 */
export function OtaUpdateSheet() {
  const { t } = useTranslate('profile');
  const { userRole } = useAuthContext();
  const status = useOtaStatus();
  const [dismissed, setDismissed] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const running = useRef(false);

  const runCheck = useCallback(
    async (force: boolean) => {
      if (!isOtaEnabled() || running.current) return;
      running.current = true;
      try {
        setOtaStatus('checking');
        const result = await checkForUpdate({ channel: getChannelForRole(userRole), force });
        if (!result.available) {
          setOtaStatus('up-to-date');
          return;
        }
        const version = result.release.version;
        setOtaStatus('found', { version });
        setOtaStatus('downloading', { version });
        await downloadAndStageUpdate(result.release);
        setOtaStatus('ready', { version });
        setDismissed(false);
      } catch (err: any) {
        setOtaStatus('error', { error: err?.message });
      } finally {
        running.current = false;
      }
    },
    [userRole]
  );

  // Launch check + re-check when the app returns to the foreground.
  useEffect(() => {
    if (!isOtaEnabled()) return undefined;
    runCheck(true);

    let remove: (() => void) | undefined;
    import('@capacitor/app')
      .then(({ App }) =>
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) runCheck(false);
        })
      )
      .then((handle) => {
        remove = () => handle.remove();
      })
      .catch(() => {});

    return () => remove?.();
  }, [runCheck]);

  const handleRestart = async () => {
    setRestarting(true);
    try {
      await applyNow();
    } catch {
      setRestarting(false);
    }
  };

  const handleLater = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setDismissed(true);
  };

  const open = status.stage === 'ready' && !dismissed && !isSnoozed();

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleLater}
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          },
        },
      }}
    >
      <Box sx={{ width: 36, height: 4, bgcolor: 'divider', borderRadius: 2, mx: 'auto', my: 1.5 }} />
      <Stack spacing={2} sx={{ px: 3, pb: 3, pt: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Iconify icon="solar:download-square-bold-duotone" width={32} sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant="h6">{t('ota.sheetTitle')}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('ota.sheetBody')}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.5}>
          <Button fullWidth variant="outlined" color="inherit" onClick={handleLater}>
            {t('ota.later')}
          </Button>
          <LoadingButton
            fullWidth
            variant="contained"
            loading={restarting}
            onClick={handleRestart}
            startIcon={<Iconify icon="solar:refresh-bold" />}
          >
            {t('ota.restartNow')}
          </LoadingButton>
        </Stack>
      </Stack>
    </Drawer>
  );
}
